import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';

/** «2026-11-03» → «03/11/2026»; anything else → ''. */
export function isoToItalian(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/** «03/11/2026» → «2026-11-03»; null when not a real calendar day. */
export function italianToIso(text: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const probe = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  const real =
    probe.getUTCFullYear() === Number(yyyy) &&
    probe.getUTCMonth() === Number(mm) - 1 &&
    probe.getUTCDate() === Number(dd);
  return real ? `${yyyy}-${mm}-${dd}` : null;
}

/** Digits typed or pasted, laid out as gg/mm/aaaa («0311» → «03/11»); the slashes come by themselves. */
export function maskItalianDate(text: string): string {
  const digits = text.replace(/\D/g, '').slice(0, 8);
  let out = digits.slice(0, 2);
  if (digits.length > 2) out += `/${digits.slice(2, 4)}`;
  if (digits.length > 4) out += `/${digits.slice(4)}`;
  return out;
}

type Props = Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type'> & {
  /** The ISO day («2026-11-03»), or '' when empty or not yet a whole valid date. */
  readonly value: string;
  readonly onChange: (iso: string) => void;
  /** What the field shows, typed or not yet a whole date, each time it changes. */
  readonly onTextChange?: (text: string) => void;
};

/**
 * A date field that always reads gg/mm/aaaa, whatever the browser's language (audit M19). The value
 * going in and out is the ISO day; a half-typed or impossible date reports '' and, once the field is
 * left, shows as invalid.
 * Since '' is also what a half-typed date reports, a clear that must also wipe a half-typed draft
 * remounts the field (a new `key`).
 */
function DateField({
  value,
  onChange,
  onTextChange,
  onBlur,
  'aria-invalid': ariaInvalid,
  ...props
}: Props) {
  const { t } = useTranslation('common');
  const [draft, setDraft] = React.useState(() => isoToItalian(value));
  const reportText = React.useRef(onTextChange);
  reportText.current = onTextChange;
  React.useEffect(() => {
    reportText.current?.(draft);
  }, [draft]);
  // A value changed from outside (a prefill, «Svuota», a route) replaces what is shown. The '' this
  // field reports for a half-typed date (a backspace on a whole one) keeps the draft as typed.
  React.useEffect(() => {
    setDraft((current) => {
      const iso = italianToIso(current);
      return iso === value || (iso === null && value === '') ? current : isoToItalian(value);
    });
  }, [value]);
  // A half-typed date is only flagged once the field is left: while typing it is not wrong yet.
  const [left, setLeft] = React.useState(false);
  const broken = left && draft !== '' && italianToIso(draft) === null;
  return (
    <Input
      {...props}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={t('dateField.placeholder')}
      maxLength={10}
      value={draft}
      aria-invalid={ariaInvalid ?? (broken || undefined)}
      onBlur={(event) => {
        setLeft(true);
        onBlur?.(event);
      }}
      onChange={(event) => {
        setLeft(false);
        // A pasted ISO day («2026-11-03») is read as that day, not as eight loose digits.
        const next = maskItalianDate(isoToItalian(event.target.value.trim()) || event.target.value);
        setDraft(next);
        onChange(italianToIso(next) ?? '');
      }}
    />
  );
}

export { DateField };
